import { useEffect, useRef, useState } from "react";
import { X, Wallet, Upload, Loader2, AlertTriangle, CheckCircle2, RefreshCw, Search, Unlock } from "lucide-react";
import { parseCSV } from "@/lib/cohort-import";
import {
  SIMPLE_HEADERS, WORKBOOK_HEADERS, rupees, todayISO, readApiError, fmtDate,
  type CreditLedger, type CreditPreview, type CreditEntry,
} from "@/lib/cohort-credits";
import { L, SummaryGrid, HistoryTable } from "@/components/credit-ledger-view";

const BASE = import.meta.env.BASE_URL;
function authHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", "x-admin-token": localStorage.getItem("admin_token") || "" };
}
type Cohort = { id: number; name: string };
type Tab = "history" | "csv" | "manual" | "allocate";

function Notice({ tone, children }: { tone: "warn" | "error" | "ok"; children: React.ReactNode }) {
  return <div role={tone === "error" ? "alert" : "status"} className={`rounded-lg border px-3 py-2 text-sm flex gap-2 ${L[tone]}`}>{tone === "ok" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}<div>{children}</div></div>;
}
function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return <label className="block text-sm"><span className="font-medium">{label}</span><div className="mt-1">{children}</div>{hint && <span className={`block text-xs mt-1 ${L.muted}`}>{hint}</span>}</label>;
}

export default function CohortCreditsDialog({ onClose, onChange }: { onClose: () => void; onChange?: () => void }) {
  const [cohorts, setCohorts] = useState<Cohort[] | null>(null);
  const [cohortId, setCohortId] = useState<number | null>(null);
  const [investorId, setInvestorId] = useState<number | null>(null);
  const [base, setBase] = useState<CreditLedger | null>(null);
  const [ledger, setLedger] = useState<CreditLedger | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<Tab>("history");
  const [search, setSearch] = useState("");
  const [release, setRelease] = useState<CreditEntry | null>(null);
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const onChangeRef = useRef(onChange); onChangeRef.current = onChange;
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${BASE}api/cohorts`, { headers: authHeaders() })
      .then(async r => { if (!r.ok) throw new Error(await readApiError(r)); return r.json(); })
      .then(b => setCohorts(Array.isArray(b) ? b : b?.cohorts || []))
      .catch(e => { setError(e.message || "Could not load cohorts."); setCohorts([]); });
    const prev = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
      if (e.key === "Tab" && dialogRef.current) {
        const f = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]):not([type="file"]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]');
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", k);
    return () => { document.removeEventListener("keydown", k); abortRef.current?.abort(); prev?.focus?.(); };
  }, []);

  const load = async (cid: number, iid: number | null) => {
    abortRef.current?.abort();
    const ctrl = new AbortController(); abortRef.current = ctrl;
    const seq = ++seqRef.current;
    setLoading(true); setError("");
    try {
      const q = `cohortId=${cid}${iid ? `&investorId=${iid}` : ""}`;
      const r = await fetch(`${BASE}api/admin/cohort-credits?${q}`, { headers: authHeaders(), signal: ctrl.signal });
      if (!r.ok) throw new Error(await readApiError(r));
      const body: CreditLedger = await r.json();
      if (seq !== seqRef.current) return;
      if (iid) setLedger(prev => ({ ...body, companies: body.companies?.length ? body.companies : prev?.companies }));
      else setBase(body);
    } catch (e) {
      if (seq !== seqRef.current || (e instanceof DOMException && e.name === "AbortError")) return;
      setError(e instanceof Error ? e.message : "Could not load the ledger.");
    } finally { if (seq === seqRef.current) setLoading(false); }
  };

  useEffect(() => {
    setInvestorId(null); setBase(null); setLedger(null); setRelease(null); setSearch("");
    if (cohortId) void load(cohortId, null);
    else { seqRef.current++; abortRef.current?.abort(); setLoading(false); }
  }, [cohortId]);
  useEffect(() => {
    setLedger(null); setRelease(null);
    if (cohortId && investorId) void load(cohortId, investorId);
  }, [investorId]);
  // Apply a mutation response only if it belongs to the current selection; keep known companies.
  const applyMutation = (cid: number, iid: number, b: CreditLedger) => {
    onChangeRef.current?.();
    if (cid !== cohortIdRef.current || iid !== investorIdRef.current) return;
    setLedger(prev => ({ ...prev, ...b, companies: b.companies?.length ? b.companies : prev?.companies || base?.companies }));
  };
  const cohortIdRef = useRef(cohortId); cohortIdRef.current = cohortId;
  const investorIdRef = useRef(investorId); investorIdRef.current = investorId;

  const members = base?.members || [];
  const companies = ledger?.companies || base?.companies || [];
  const member = members.find(m => m.id === investorId) || null;
  const filtered = members.filter(m => `${m.name} ${m.email}`.toLowerCase().includes(search.toLowerCase()));
  const refresh = () => { onChangeRef.current?.(); if (cohortId) void load(cohortId, investorId); };
  const cohortName = cohorts?.find(c => c.id === cohortId)?.name || "";

  const tabs: [Tab, string][] = [["history", "History"], ["csv", "Import CSV"], ["manual", "Manual payment / top-up"], ["allocate", "Allocate"]];

  return (
    <div className="fixed inset-0 z-[10000] bg-[#0B0907]/75 flex items-end sm:items-center justify-center sm:p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="cc-title" data-testid="dialog-cohort-credits"
        className={`${L.panel} border ${L.border} rounded-t-2xl sm:rounded-2xl w-full max-w-6xl max-h-[95dvh] flex flex-col shadow-2xl outline-none [overflow-wrap:anywhere]`}>
        <div className={`border-b ${L.border} px-5 py-4 flex items-start justify-between gap-4`}>
          <div>
            <h2 id="cc-title" className="text-xl font-bold flex items-center gap-2"><Wallet className="w-5 h-5 text-[#C64600] dark:text-[#FF7A2E]" /> Payments &amp; credits</h2>
            <p className={`${L.muted} text-sm mt-0.5`}>Cohort credit ledger. CSV imports and manual payments are previewed before you confirm; allocations and releases are recorded as soon as you submit. Every action needs a unique reference. No emails are sent.</p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${L.ghost}`} aria-label="Close" data-testid="button-close-credits"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <Notice tone="warn"><strong>Historic investments are not reconciled.</strong> Existing investments are left untouched and are not automatically deducted from these credits. If you import historic payments, reconcile past allocations by hand before telling anyone what remains.</Notice>
          {error && <Notice tone="error">{error} {cohortId && <button className="underline font-semibold ml-1" onClick={refresh}>Retry</button>}</Notice>}

          <div className="grid md:grid-cols-[260px_1fr] gap-4">
            <aside className="space-y-3">
              <Field label="Cohort">
                {cohorts === null ? <div className={`h-9 rounded-lg animate-pulse ${L.sub}`} /> : (
                  <select value={cohortId ?? ""} onChange={e => setCohortId(e.target.value ? Number(e.target.value) : null)} className={L.input} data-testid="select-credit-cohort">
                    <option value="">Select a cohort…</option>
                    {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
              </Field>
              {cohortId && (
                <div className="space-y-2">
                  <div className="relative"><Search className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${L.muted}`} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Find a member" className={`${L.input} pl-8`} data-testid="input-member-search" /></div>
                  <div className={`border ${L.border} rounded-xl max-h-[340px] overflow-y-auto divide-y divide-[#D9D1C3] dark:divide-[#3A332A]`}>
                    {!base && loading ? [0, 1, 2].map(i => <div key={i} className={`h-12 animate-pulse ${L.sub}`} />) :
                      filtered.length === 0 ? <p className={`p-4 text-sm ${L.muted}`}>{members.length ? "No match." : "No members in this cohort."}</p> :
                        filtered.map(m => (
                          <button key={m.id} onClick={() => setInvestorId(m.id)} data-testid={`button-member-${m.id}`}
                            className={`w-full text-left px-3 py-2 ${m.id === investorId ? "bg-[#FDEBDD] dark:bg-[#3A1F0E]" : "hover:bg-[#EDE7DC] dark:hover:bg-[#2C261E]"}`}>
                            <span className="block text-sm font-semibold truncate">{m.name}</span>
                            <span className={`block text-xs truncate ${L.muted}`}>{m.email}</span>
                          </button>
                        ))}
                  </div>
                </div>
              )}
            </aside>

            <section className="min-w-0 space-y-4">
              {!cohortId ? <div className={`rounded-xl border ${L.border} ${L.sub} p-10 text-center text-sm ${L.muted}`}>Choose a cohort to begin.</div> : (
                <>
                  <div className="flex flex-wrap gap-1" role="tablist">
                    {tabs.map(([k, label]) => (
                      <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium ${tab === k ? "bg-[#C64600] text-[#FFF8F2]" : L.ghost}`}>{label}</button>
                    ))}
                  </div>

                  {tab === "csv" && <CsvImport key={`csv-${cohortId}`} cohortId={cohortId} cohortName={cohortName} onDone={refresh} />}
                  {tab === "manual" && (member ? <ManualEntry key={`m-${cohortId}-${member.id}`} cohortId={cohortId} cohortName={cohortName} member={member} onDone={refresh} /> : <PickHint />)}
                  {(tab === "history" || tab === "allocate") && !investorId && <PickHint />}
                  {(tab === "history" || tab === "allocate") && investorId && (
                    !ledger ? (error ? null : <div className={`h-40 rounded-xl animate-pulse ${L.sub}`} />) : ledger.summary ? (
                      <div className="space-y-4">
                        <h3 className="font-semibold">{member?.name} <span className={`text-sm font-normal ${L.muted}`}>in {cohortName}</span></h3>
                        <SummaryGrid s={ledger.summary} />
                        {tab === "allocate"
                          ? <AllocateForm key={`a-${cohortId}-${investorId}`} cohortId={cohortId} investorId={investorId} available={ledger.summary} companies={companies} onDone={b => applyMutation(cohortId, investorId, b)} />
                          : <>
                            {release && <ReleaseForm key={`r-${cohortId}-${investorId}-${release.id}`} entry={release} cohortId={cohortId} investorId={investorId} onCancel={() => setRelease(null)} onDone={b => { applyMutation(cohortId, investorId, b); setRelease(null); }} />}
                            <HistoryTable entries={ledger.entries} companies={companies} onRelease={setRelease} />
                          </>}
                      </div>
                    ) : <div className={`rounded-xl border ${L.border} ${L.sub} p-6 text-sm ${L.muted}`}>No ledger data returned for this member.</div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function PickHint() {
  return <div className={`rounded-xl border ${L.border} ${L.sub} p-8 text-center text-sm ${L.muted}`}>Select a member on the left.</div>;
}

function PreviewCommit({ cohortId, cohortName, rows, onDone, onReset }: { cohortId: number; cohortName: string; rows: Record<string, string>[]; onDone: () => void; onReset?: () => void }) {
  const [preview, setPreview] = useState<CreditPreview | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);
  const key = JSON.stringify(rows);
  useEffect(() => { setPreview(null); setSelected(new Set()); setAck(false); setResult(null); setError(""); }, [key]);

  const runPreview = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await fetch(`${BASE}api/admin/cohort-credits/preview`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ cohortId, rows }) });
      if (!r.ok) throw new Error(await readApiError(r));
      const p: CreditPreview = await r.json();
      setPreview(p); setSelected(new Set(p.rows.filter(x => x.status === "ready").map(x => x.index))); setAck(false);
    } catch (e) { setError(e instanceof Error ? e.message : "Preview failed."); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!preview) return;
    setBusy(true); setError("");
    try {
      const r = await fetch(`${BASE}api/admin/cohort-credits/commit`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ cohortId, rows, reviewHash: preview.reviewHash, selectedRows: [...selected] }) });
      if (r.status === 409) { setPreview(null); throw new Error("Data changed since this preview. Nothing was saved. Preview again."); }
      if (!r.ok) throw new Error(`Nothing was saved. ${await readApiError(r)}`);
      setResult(await r.json()); setPreview(null); onDone(); onReset?.();
    } catch (e) { setError(e instanceof Error ? e.message : "Commit failed."); }
    finally { setBusy(false); }
  };
  const tone = { ready: L.ok, duplicate: L.warn, blocked: L.error };

  return (
    <div className="space-y-3">
      {error && <Notice tone="error">{error}</Notice>}
      {result && <Notice tone="ok">Recorded {result.imported} row(s); {result.skipped} skipped. No emails were sent.</Notice>}
      {!preview ? (
        <button onClick={runPreview} disabled={busy || rows.length === 0} className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${L.primary}`} data-testid="button-credit-preview">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Preview {rows.length} row{rows.length === 1 ? "" : "s"}
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-sm" data-testid="text-credit-preview-summary">Ready <strong>{preview.summary.ready}</strong> · Duplicate <strong>{preview.summary.duplicate}</strong> · Blocked <strong>{preview.summary.blocked}</strong></p>
          <ul className="space-y-2">
            {preview.rows.map(r => (
              <li key={r.index} className={`rounded-xl border ${L.border} p-3 flex gap-3`} data-testid={`row-credit-preview-${r.index}`}>
                <input type="checkbox" className="mt-1 w-4 h-4 accent-[#C64600]" disabled={r.status !== "ready"} checked={selected.has(r.index)}
                  onChange={() => { setSelected(s => { const n = new Set(s); n.has(r.index) ? n.delete(r.index) : n.add(r.index); return n; }); setAck(false); }} aria-label={`Include row ${r.rowNumber}`} />
                <div className="min-w-0 flex-1 text-sm">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className={`font-mono text-xs ${L.muted}`}>Row {r.rowNumber}</span><strong>{r.name || "(no name)"}</strong><span className={L.muted}>{r.email}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${tone[r.status]}`}>{r.status === "ready" ? "Ready" : r.status === "duplicate" ? "Duplicate" : "Blocked"}</span>
                  </div>
                  <p className={`text-xs font-mono mt-1 ${L.muted}`}>Paid {rupees(r.payment)} · Included {rupees(r.included)} · Added {rupees(r.added)} · Conditional {rupees(r.conditional)}</p>
                  {r.issues.length > 0 && <ul className="text-xs list-disc pl-4 mt-1 text-[#7A1D12] dark:text-[#F4B3A9]">{r.issues.map((s, i) => <li key={i}>{s}</li>)}</ul>}
                </div>
              </li>
            ))}
          </ul>
          <div className="rounded-xl border-2 border-[#C64600] p-4 space-y-3 bg-[#FDF1E7] dark:bg-[#2A1A0E]">
            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#C64600]" checked={ack} disabled={selected.size === 0} onChange={e => setAck(e.target.checked)} data-testid="checkbox-credit-ack" />
              <span>I confirm only the <strong>{selected.size}</strong> selected row(s) will be recorded in <strong>{cohortName}</strong>. No emails will be sent, and no historical reconciliation is performed: existing investments are not deducted from these credits.</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <button onClick={commit} disabled={!ack || selected.size === 0 || busy} className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${L.primary}`} data-testid="button-credit-commit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Record {selected.size} row{selected.size === 1 ? "" : "s"}
              </button>
              <button onClick={() => setPreview(null)} disabled={busy} className={`px-4 py-2 rounded-lg text-sm ${L.ghost}`}>Discard preview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CsvImport({ cohortId, cohortName, onDone }: { cohortId: number; cohortName: string; onDone: () => void }) {
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  const onFile = async (f: File) => {
    setErr("");
    try {
      const { headers, rows: parsed } = parseCSV((await f.text()).replace(/^\uFEFF/, ""));
      const h = headers.map(x => x.trim());
      const match = (set: readonly string[]) => set.length === h.length && set.every((x, i) => x === h[i]);
      if (!match(SIMPLE_HEADERS) && !match(WORKBOOK_HEADERS)) { setErr("Headers do not match either credit template exactly. Use the simple template or the original workbook export without renaming columns."); return; }
      const data = parsed.filter(r => Object.values(r).some(v => String(v).trim()));
      if (!data.length) { setErr("The file has headers but no data rows."); return; }
      setRows(data); setName(f.name);
    } catch { setErr("Could not read this file. Save it as CSV UTF-8 and try again."); }
  };
  return (
    <div className="space-y-3">
      <p className={`text-sm ${L.muted}`}>Accepts the simple credit template (“Member name”, “Registered email”…) or the original workbook export (“investor_name”, “investor_email”…). The file is read in this browser; nothing is saved until you confirm.</p>
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ""; }} data-testid="input-credit-csv" />
      <button onClick={() => ref.current?.click()} className={`px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 ${L.ghost}`} data-testid="button-choose-credit-csv"><Upload className="w-4 h-4" /> {rows.length ? "Replace CSV" : "Choose CSV"}</button>
      {name && <p className="text-sm">{name}: <strong>{rows.length}</strong> row(s)</p>}
      {err && <Notice tone="error">{err}</Notice>}
      {rows.length > 0 && <PreviewCommit cohortId={cohortId} cohortName={cohortName} rows={rows} onDone={onDone} onReset={() => { setRows([]); setName(""); }} />}
    </div>
  );
}

function ManualEntry({ cohortId, cohortName, member, onDone }: { cohortId: number; cohortName: string; member: { id: number; name: string; email: string }; onDone: () => void }) {
  const [f, setF] = useState({ reference: "", date: todayISO(), kind: "Payment received", payment: "", included: "", added: "", usable: "Yes", conditions: "", notes: "" });
  const [rows, setRows] = useState<Record<string, string>[] | null>(null);
  const set = (k: keyof typeof f, v: string) => { setF(p => ({ ...p, [k]: v })); setRows(null); };
  const build = () => {
    const H = SIMPLE_HEADERS;
    setRows([{
      [H[0]]: member.name, [H[1]]: member.email, [H[2]]: f.reference.trim(), [H[3]]: f.date, [H[4]]: f.kind,
      [H[5]]: f.kind === "Credit only" ? "0" : f.payment.trim(), [H[6]]: f.included.trim() || "0", [H[7]]: f.added.trim() || "0",
      [H[8]]: f.usable, [H[9]]: f.usable === "Not yet" ? f.conditions.trim() : "", [H[10]]: f.notes.trim(),
    }]);
  };
  const valid = f.reference.trim() && f.date && (f.kind === "Credit only" || f.payment) && (f.usable !== "Not yet" || f.conditions.trim());
  return (
    <div className="space-y-3">
      <p className={`text-sm ${L.muted}`}>Recording for <strong className="text-[#1F1B16] dark:text-[#F2EDE4]">{member.name}</strong>. Enter only the new amounts. Credits are recorded exactly as typed and are never derived from the payment.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Unique reference"><input value={f.reference} onChange={e => set("reference", e.target.value)} placeholder="PAY-2026-014" className={L.input} data-testid="input-manual-reference" /></Field>
        <Field label="Date"><input type="date" value={f.date} onChange={e => set("date", e.target.value)} className={L.input} data-testid="input-manual-date" /></Field>
        <Field label="What are you recording?"><select value={f.kind} onChange={e => set("kind", e.target.value)} className={L.input} data-testid="select-manual-kind"><option>Payment received</option><option>Credit only</option></select></Field>
        <Field label="Money received (₹)"><input type="number" min="0" disabled={f.kind === "Credit only"} value={f.kind === "Credit only" ? "0" : f.payment} onChange={e => set("payment", e.target.value)} className={L.input} data-testid="input-manual-payment" /></Field>
        <Field label="Included allocation credit (₹)"><input type="number" min="0" value={f.included} onChange={e => set("included", e.target.value)} className={L.input} data-testid="input-manual-included" /></Field>
        <Field label="Favcy added allocation credit (₹)"><input type="number" min="0" value={f.added} onChange={e => set("added", e.target.value)} className={L.input} data-testid="input-manual-added" /></Field>
        <Field label="Can the extra credit be used now?"><select value={f.usable} onChange={e => set("usable", e.target.value)} className={L.input} data-testid="select-manual-usable"><option>Yes</option><option>Not yet</option><option>No extra credit</option></select></Field>
        {f.usable === "Not yet" && <Field label="What must happen first?"><input value={f.conditions} onChange={e => set("conditions", e.target.value)} placeholder="Signed offer addendum received" className={L.input} data-testid="input-manual-conditions" /></Field>}
        <div className="sm:col-span-2"><Field label="Notes"><textarea rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} className={L.input} data-testid="input-manual-notes" /></Field></div>
      </div>
      {!rows ? <button onClick={build} disabled={!valid} className={`px-4 py-2 rounded-lg text-sm font-semibold ${L.primary}`} data-testid="button-manual-prepare">Prepare for preview</button>
        : <PreviewCommit cohortId={cohortId} cohortName={cohortName} rows={rows} onDone={onDone} onReset={() => { setRows(null); setF(p => ({ ...p, reference: "", payment: "", included: "", added: "", conditions: "", notes: "" })); }} />}
    </div>
  );
}

function AllocateForm({ cohortId, investorId, available, companies, onDone }: { cohortId: number; investorId: number; available: { remainingIncluded: number; remainingAdded: number }; companies: { id: number; name: string }[]; onDone: (b: CreditLedger) => void }) {
  const [f, setF] = useState({ companyId: "", reference: "", date: todayISO(), includedAmount: "", addedAmount: "", notes: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [ok, setOk] = useState("");
  const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }));
  const submit = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const r = await fetch(`${BASE}api/admin/cohort-credits/allocate`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ cohortId, investorId, companyId: Number(f.companyId), reference: f.reference.trim(), date: f.date, includedAmount: f.includedAmount.trim() || "0", addedAmount: f.addedAmount.trim() || "0", notes: f.notes.trim() }) });
      if (!r.ok) throw new Error(await readApiError(r));
      onDone(await r.json()); setOk(`Allocation ${f.reference.trim()} recorded.`);
      setF(p => ({ ...p, reference: "", includedAmount: "", addedAmount: "", notes: "" }));
    } catch (e) { setErr(e instanceof Error ? e.message : "Allocation failed."); }
    finally { setBusy(false); }
  };
  const valid = f.companyId && f.reference.trim() && f.date && (Number(f.includedAmount) > 0 || Number(f.addedAmount) > 0);
  return (
    <div className={`rounded-xl border ${L.border} p-4 space-y-3`}>
      <h4 className="font-semibold">Apply credit to a company</h4>
      <p className={`text-xs ${L.muted}`}>Draws from remaining usable credit: {rupees(available.remainingIncluded)} included, {rupees(available.remainingAdded)} added. Conditional credit cannot be used until released.</p>
      {err && <Notice tone="error">{err}</Notice>}{ok && <Notice tone="ok">{ok}</Notice>}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Company"><select value={f.companyId} onChange={e => set("companyId", e.target.value)} className={L.input} data-testid="select-allocate-company"><option value="">Select…</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        <Field label="Unique reference"><input value={f.reference} onChange={e => set("reference", e.target.value)} placeholder="ALLOC-2026-003" className={L.input} data-testid="input-allocate-reference" /></Field>
        <Field label="Date"><input type="date" value={f.date} onChange={e => set("date", e.target.value)} className={L.input} data-testid="input-allocate-date" /></Field>
        <div />
        <Field label="From included credit (₹)"><input type="number" min="0" value={f.includedAmount} onChange={e => set("includedAmount", e.target.value)} className={L.input} data-testid="input-allocate-included" /></Field>
        <Field label="From added credit (₹)"><input type="number" min="0" value={f.addedAmount} onChange={e => set("addedAmount", e.target.value)} className={L.input} data-testid="input-allocate-added" /></Field>
        <div className="sm:col-span-2"><Field label="Notes"><textarea rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} className={L.input} data-testid="input-allocate-notes" /></Field></div>
      </div>
      <button onClick={submit} disabled={!valid || busy} className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${L.primary}`} data-testid="button-allocate-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Record allocation</button>
    </div>
  );
}

function ReleaseForm({ entry, cohortId, investorId, onCancel, onDone }: { entry: CreditEntry; cohortId: number; investorId: number; onCancel: () => void; onDone: (b: CreditLedger) => void }) {
  const [f, setF] = useState({ reference: "", date: todayISO(), notes: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${BASE}api/admin/cohort-credits/release`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ cohortId, investorId, entryId: entry.id, reference: f.reference.trim(), date: f.date, notes: f.notes.trim() }) });
      if (!r.ok) throw new Error(await readApiError(r));
      onDone(await r.json());
    } catch (e) { setErr(e instanceof Error ? e.message : "Release failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border-2 border-[#C64600] p-4 space-y-3 bg-[#FDF1E7] dark:bg-[#2A1A0E]" data-testid="form-release">
      <h4 className="font-semibold flex items-center gap-2"><Unlock className="w-4 h-4" /> Release conditional credit from {entry.reference}</h4>
      <p className="text-sm">This makes the full conditional grant of <strong>{rupees(entry.conditional)}</strong> (recorded {fmtDate(entry.date)}) usable now. It is <strong>not</strong> an extra credit award; the total credit does not increase.{entry.conditions && <> Condition: “{entry.conditions}”.</>}</p>
      {err && <Notice tone="error">{err}</Notice>}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Unique reference"><input value={f.reference} onChange={e => setF(p => ({ ...p, reference: e.target.value }))} placeholder="REL-2026-001" className={L.input} data-testid="input-release-reference" /></Field>
        <Field label="Date"><input type="date" value={f.date} onChange={e => setF(p => ({ ...p, date: e.target.value }))} className={L.input} data-testid="input-release-date" /></Field>
        <div className="sm:col-span-2"><Field label="Notes (how the condition was met)"><textarea rows={2} value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} className={L.input} data-testid="input-release-notes" /></Field></div>
      </div>
      <div className="flex gap-2">
        <button onClick={submit} disabled={!f.reference.trim() || !f.date || busy} className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${L.primary}`} data-testid="button-release-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Release full grant</button>
        <button onClick={onCancel} className={`px-4 py-2 rounded-lg text-sm ${L.ghost}`} data-testid="button-release-cancel">Cancel</button>
      </div>
    </div>
  );
}
